import { FC } from "react";
import { ReceiptRow } from "./ReceiptRow";
import styles from "./ReceiptContent.module.scss";
import { formatMoney } from "@/helpers/numbers";
import { useTranslation } from "react-i18next";
import Discounts from "./discounts";
import { Product } from "types/product";
import { logFirebaseEvent } from "services/firebase";
import { playSound, SoundOption } from "@/helpers/playSound";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { addProduct, removeProduct } from "@/store/cart/thunk";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectCartProducts, selectCartTotal } from "@/store/cart/selectors";
import { reshowCombiDealModal } from "@/store/ui/slice";

const ReceiptContent: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const items = useAppSelector(selectCartProducts);
  const cartTotal = useAppSelector(selectCartTotal);

  const onIncrement = (productId: Product["id"]) => {
    playSound(SoundOption.Click1Hi);
    dispatch(addProduct(productId));
    logFirebaseEvent("product_incremented", {
      id: productId,
    });
  };

  const onDecrement = (productId: Product["id"]) => {
    playSound(SoundOption.Click2);
    dispatch(removeProduct(productId));
    logFirebaseEvent("product_decremented", {
      id: productId,
    });
  };

  const onRemoveAll = (productId: Product["id"], quantity: number) => {
    playSound(SoundOption.Remove);
    dispatch(removeProduct(productId, quantity));
    logFirebaseEvent("product_removed", {
      id: productId,
    });
  };

  const onReshowDealModal = (product: Product["id"]) => {
    dispatch(reshowCombiDealModal(product));
  };

  return (
    <div className={styles.container}>
      <div className={styles.column}>
        {items.map(({ productId, ...item }) => (
          <ReceiptRow
            key={productId}
            translations={item.translations}
            onIncrement={() => onIncrement(productId)}
            onDecrement={() => onDecrement(productId)}
            onRemoveAll={() => onRemoveAll(productId, item.quantity)}
            pricePerUnit={item.priceCents}
            quantity={item.quantity}
            combiDealAvailable={!!item.combiDeals.length}
            combiDealApplied={item.combiDealApplied}
            onReshowDealModal={() => onReshowDealModal(productId)}
          />
        ))}
      </div>
      <div>
        <Discounts />
        <div className={styles.divider} />
        <div className={styles.totalWrapper}>
          <p className="h4">{t("ui.total")}</p>
          <p className="h4">{formatMoney(cartTotal / 100)}</p>
        </div>
      </div>
    </div>
  );
};

export default ReceiptContent;
